/**
 * Map screens.overview API payload → IntelligenceInsightsOverview view-model.
 * Keeps the Figma Overview props stable while the API remains contract-shaped.
 */

import { MODULE_CATEGORY_ORDER, OVERVIEW_VARIANTS } from '../components/LensIntelligence/IntelligenceInsights/overviewDummyData';

const GROUP_TO_UI_CATEGORY = {
    reporting: 'reporting',
    strategic: 'strategic_analysis',
    strategic_analysis: 'strategic_analysis',
    validation: 'validation',
};

function titleCaseToken(value) {
    if (!value) return '';
    return String(value)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, ch => ch.toUpperCase());
}

function formatGeneratedLabel(iso) {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return `Generated ${d.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    })}`;
}

function mapModuleStatus(module) {
    if (module?.new_evidence_available && ['complete', 'complete_with_warnings'].includes(module.status)) {
        return 'new_evidence';
    }
    if (module?.status === 'complete_with_warnings') return 'warnings';
    if (module?.status === 'pending' || module?.status === 'not_generated') return 'pending';
    if (module?.status === 'failed') return 'pending';
    if (module?.status === 'generating') return 'pending';
    return 'complete';
}

function mapModuleCard(module) {
    const indicator = module.primary_indicator || {};
    const status = mapModuleStatus(module);
    const card = {
        key: module.module_key,
        title: module.title,
        category: GROUP_TO_UI_CATEGORY[module._grouping] || module.category || 'reporting',
        description: module.purpose,
        status,
        warningNote: module.warning_note || null,
        openLabel: status === 'new_evidence' ? 'Open current →' : 'Open →',
        secondaryActionLabel: status === 'new_evidence' ? 'Generate new version' : null,
    };

    if (indicator.kind === 'rag' && indicator.display) {
        card.metric = null;
        card.metricPrefix = 'Overall RAG ·';
        card.ragPill = {
            label: indicator.display,
            tone: indicator.tone || String(indicator.value || 'grey').toLowerCase(),
        };
    } else if (indicator.kind === 'posture' && indicator.display) {
        card.metric = null;
        card.posturePill = {
            label: indicator.display,
            tone: indicator.tone || 'amber',
        };
    } else if (indicator.display) {
        card.metric = indicator.display;
    }

    if (module.confidence || module.evidence_coverage) {
        const conf = titleCaseToken(module.confidence);
        const cov = titleCaseToken(module.evidence_coverage);
        if (conf && cov) {
            card.confidence = `${conf} confidence · ${cov} evidence`;
        } else if (conf) {
            card.confidence = `${conf} confidence`;
        }
    }

    return card;
}

function flattenModules(moduleGroups = []) {
    const modules = [];
    for (const group of moduleGroups) {
        const grouping = group.grouping;
        for (const mod of group.modules || []) {
            modules.push(mapModuleCard({ ...mod, _grouping: grouping }));
        }
    }
    // Preserve UI category order for any modules missing from API.
    const byKey = new Map(modules.map(m => [m.key, m]));
    const ordered = [];
    for (const section of MODULE_CATEGORY_ORDER) {
        for (const mod of modules) {
            if (mod.category === section.key && byKey.has(mod.key)) {
                ordered.push(byKey.get(mod.key));
                byKey.delete(mod.key);
            }
        }
    }
    for (const leftover of byKey.values()) ordered.push(leftover);
    return ordered;
}

function mapSourceSnapshotView(payload) {
    if (!payload || typeof payload !== 'object') return null;
    const mapRow = row => ({
        id: row.id,
        title: row.title,
        meta: row.meta,
        sourceType: row.source_type || row.sourceType || null,
        workstreamId: row.workstream_id ?? row.workstreamId ?? null,
        wsFolderId: row.ws_folder_id ?? row.wsFolderId ?? null,
        status: row.status,
        statusLabel: row.status_label || row.statusLabel || null,
        impact: row.impact || null,
    });
    const mapGroup = group => ({
        key: group.key,
        label: group.label,
        count: group.count,
        countLabel: group.count_label || group.countLabel,
        preview: (group.preview || []).map(mapRow),
        moreCount: group.more_count ?? group.moreCount ?? 0,
        moreLabel: group.more_label || group.moreLabel || null,
    });
    return {
        header: {
            titleLine: payload.header?.title_line || payload.header?.titleLine || '',
            generatedLabel: payload.header?.generated_label || payload.header?.generatedLabel || null,
            snapshotId: payload.header?.snapshot_id || payload.header?.snapshotId || null,
        },
        summary: payload.summary || {},
        includedGroups: (payload.included_groups || payload.includedGroups || []).map(mapGroup),
        excludedGroups: (payload.excluded_groups || payload.excludedGroups || [])
            .filter(g => (g.items || []).length)
            .map(g => ({
                key: g.key,
                label: g.label,
                items: (g.items || []).map(mapRow),
            })),
        footerNote: payload.footer_note || payload.footerNote || '',
        catalogTruncated: Boolean(payload.catalog_truncated ?? payload.catalogTruncated),
    };
}

function mapBanner(banner) {
    if (!banner || banner.type === 'none') return { warningBanner: null, newEvidenceBanner: null };
    const mapped = {
        title: banner.title,
        message: banner.message,
        source: banner.source || null,
        affected: banner.affected || null,
        note: banner.note || null,
        actions: Array.isArray(banner.actions) ? banner.actions : [],
    };
    if (banner.type === 'warnings' || banner.type === 'failure') {
        return { warningBanner: mapped, newEvidenceBanner: null };
    }
    if (banner.type === 'new_evidence' || banner.type === 'processing') {
        return { warningBanner: null, newEvidenceBanner: mapped };
    }
    return { warningBanner: null, newEvidenceBanner: null };
}

function resolveVariant(payload) {
    const status = payload?.analysis?.status;
    const ready = payload?.data?.live_ready_source_count ?? payload?.data?.source_readiness?.totals?.ready ?? 0;
    const bannerType = payload?.data?.conditional_banner?.type;

    if (status === 'not_generated' || !status) {
        if (Number(ready) <= 0) return OVERVIEW_VARIANTS.NO_READY_SOURCES;
        return OVERVIEW_VARIANTS.NOT_GENERATED;
    }
    if (bannerType === 'warnings' || status === 'complete_with_warnings') {
        return OVERVIEW_VARIANTS.WARNINGS;
    }
    if (bannerType === 'new_evidence' || payload?.new_evidence?.available) {
        return OVERVIEW_VARIANTS.NEW_EVIDENCE;
    }
    return OVERVIEW_VARIANTS.GENERATED;
}

/**
 * @param {object} payload screens.overview response
 * @returns {object} view-model consumed by IntelligenceInsightsOverview
 */
export function overviewPayloadToViewModel(payload) {
    if (!payload || typeof payload !== 'object') {
        return null;
    }

    const variant = resolveVariant(payload);
    const data = payload.data || {};
    const analysis = payload.analysis || {};
    const context = payload.context || {};
    const trust = payload.trust || {};
    const current = data.current_analysis;
    const contextSummary = data.context_summary || {};
    const readiness = data.source_readiness || {};
    const totals = readiness.totals || {};
    const counts = contextSummary.source_type_counts || context.source_type_counts || {};
    const modules = flattenModules(data.module_groups || []);
    const banners = mapBanner(data.conditional_banner);

    const conf = titleCaseToken(trust.confidence || current?.overall_confidence);
    const cov = titleCaseToken(trust.evidence_coverage || current?.overall_evidence_coverage);
    const confidenceLine =
        conf && cov ? `${conf} confidence · ${cov} evidence` : conf ? `${conf} confidence` : null;

    const enabled = current?.module_summary?.enabled ?? modules.length;
    const available = current?.module_summary?.available ?? 0;
    const versionLabel = current?.version_label || (analysis.version != null ? `Version ${analysis.version}` : null);
    const generatedLabel = formatGeneratedLabel(current?.generated_at || analysis.generated_at);
    const contextLabel = current?.context_label || contextSummary.label || context.label || 'Entire Project';

    const primaryAction =
        (payload.module_actions || []).find(a =>
            ['generate_analysis', 'generate_new_version'].includes(a.key),
        ) || (payload.module_actions || [])[0];

    const secondary = data.secondary_views || {};
    const secondaryViews = {
        versions: Array.isArray(secondary.version_history) ? secondary.version_history : [],
        analysisDetails: Array.isArray(secondary.analysis_details)
            ? secondary.analysis_details
            : [],
        moduleConfig: (Array.isArray(secondary.module_configuration)
            ? secondary.module_configuration
            : []
        ).map(m => ({
            key: m.key,
            title: m.title,
            description: m.description,
            projectType: m.project_type || m.projectType || '—',
            enabled: Boolean(m.enabled),
        })),
        generateModal: secondary.generate_preview
            ? {
                  title: secondary.generate_preview.title,
                  rows: secondary.generate_preview.rows || [],
                  note: secondary.generate_preview.note || '',
                  confirmLabel: secondary.generate_preview.confirm_label || 'Generate →',
              }
            : null,
        sourceReadiness: {
            summary: {
                ready: Number(totals.ready || 0),
                processing: Number(totals.processing || 0),
                failed: Number(totals.failed || 0),
            },
            tabs: readiness.tabs || ['All', 'Ready', 'Processing', 'Failed', 'Material impact'],
            sources: (readiness.sources || []).map(s => ({
                id: s.id,
                title: s.title,
                meta: s.meta,
                sourceType: s.source_type || s.sourceType || null,
                workstreamId: s.workstream_id ?? s.workstreamId ?? null,
                wsFolderId: s.ws_folder_id ?? s.wsFolderId ?? null,
                status: s.status,
                statusLabel: s.status_label || s.statusLabel || titleCaseToken(s.status),
                impact: s.impact || null,
            })),
        },
        sourceSnapshot: mapSourceSnapshotView(secondary.source_snapshot),
    };

    const base = {
        variant,
        subtitle:
            'Evidence-backed technology analysis across reporting, strategic assessment and validation.',
        modules,
        shellSources: {
            ready: Number(totals.ready || 0),
            processing: Number(totals.processing || 0),
        },
        primaryActionLabel: primaryAction?.label || 'Generate analysis',
        moduleActions: payload.module_actions || [],
        analysisId: analysis.analysis_id || null,
        analysisVersion: analysis.version ?? null,
        secondaryViews,
        ...banners,
    };

    if (variant === OVERVIEW_VARIANTS.NO_READY_SOURCES) {
        return {
            ...base,
            hasAnalysis: false,
            metaLine: `${contextLabel} · Not yet generated`,
            noReadySources: {
                title: 'NO READY SOURCES',
                status: 'UNAVAILABLE',
                message:
                    'Lens cannot generate Intelligence Insights because no sources are currently ready for analysis.',
                readiness: `${totals.ready || 0} ready · ${totals.processing || 0} processing · ${totals.failed || 0} failed`,
                note: 'Processing sources will be included once ready.',
                actionLabel: 'Review project sources',
            },
        };
    }

    if (variant === OVERVIEW_VARIANTS.NOT_GENERATED) {
        return {
            ...base,
            hasAnalysis: false,
            metaLine: `${contextLabel} · Not yet generated`,
            generateCta: {
                title: 'Turn project evidence into structured intelligence',
                description:
                    'Lens will analyse ready VDR documents, IRL responses and consultant observations to create seven evidence-backed insight modules.',
                rows: [
                    { label: 'Selected context', value: contextLabel },
                    {
                        label: 'Source readiness',
                        value: `${totals.ready || 0} ready · ${totals.processing || 0} processing · ${totals.failed || 0} failed`,
                    },
                    { label: 'Enabled modules', value: String(enabled) },
                ],
                note: readiness.note || 'Processing and failed sources will be excluded.',
            },
        };
    }

    const processingCount = Number(totals.processing || 0);
    const failedCount = Number(totals.failed || 0);
    const materialUnavailable = processingCount + failedCount;
    const analysisWarningCount = (payload.warnings || []).length;
    // Stat sits next to Ready sources — count excluded material sources, not
    // the number of warning *records* on the version (e.g. one processing + one
    // failed code ⇒ 2 records but N sources).
    const warningCount =
        materialUnavailable > 0 ? materialUnavailable : analysisWarningCount;

    const analysisStatusKey =
        analysis.status === 'complete_with_warnings' || variant === OVERVIEW_VARIANTS.WARNINGS
            ? 'warnings'
            : 'complete';

    return {
        ...base,
        hasAnalysis: true,
        contextLabel,
        versionLabel,
        generatedLabel,
        metaLine: [contextLabel, versionLabel, generatedLabel].filter(Boolean).join(' · '),
        modulesAvailable: `${available} of ${enabled}`,
        modulesStat: {
            value: `${available} / ${enabled}`,
            label: 'Modules',
            sublabel: 'available',
        },
        readyStat: {
            value: String(totals.ready ?? current?.ready_source_count ?? 0),
            label: 'Ready',
            sublabel: 'sources',
        },
        warningStat: {
            value: String(
                current?.warning_count != null ? current.warning_count : warningCount,
            ),
            label:
                Number(current?.warning_count != null ? current.warning_count : warningCount) === 1
                    ? 'Warning'
                    : 'Warnings',
            sublabel: null,
        },
        confidence: confidenceLine,
        analysisStatus: {
            key: analysisStatusKey,
            label: analysisStatusKey === 'warnings' ? 'Complete with warnings' : 'Complete',
        },
        analysisContext: {
            title: contextSummary.label || contextLabel,
            description: contextSummary.description || '',
            breakdown: `VDR ${counts.vdr || 0} · IRL ${counts.irl || 0} · Observations ${counts.observations || 0}`,
        },
        sourceReadiness: {
            ready: Number(totals.ready || 0),
            processing: processingCount,
            failed: failedCount,
            note: readiness.note || '',
        },
    };
}

export default overviewPayloadToViewModel;
